import React from 'react';
import './LLMIcon.css';

/**
 * Provider icons — indexed by provider key.
 */
const PROVIDER_ICONS = {
  anthropic: { icon: '/logo/anthropic.png', label: 'Anthropic' },
  openai:    { icon: '/logo/openai.png', label: 'OpenAI' },
  google:    { icon: '/logo/google.png', label: 'Google' },
  deepseek:  { icon: '/logo/deepseek.webp', label: 'DeepSeek' },
  alibaba:   { icon: '/logo/alibaba.png', label: 'Alibaba Qwen' },
  moonshot:  { icon: '/logo/moonshot.avif', label: 'Moonshot Kimi' },
  zhipu:     { icon: '/logo/zhipu.png', label: 'Zhipu GLM' },
};

/**
 * Model → provider mapping.
 * When a model name is given, look up the provider first, then use the provider icon.
 */
const MODEL_TO_PROVIDER = {
  // DeepSeek
  'deepseek-v4-pro': 'deepseek',
  'deepseek-v4':     'deepseek',
  'deepseek-v3':     'deepseek',
  'deepseek-r1':     'deepseek',
  // Kimi / Moonshot
  'kimi-k2.6': 'moonshot',
  'kimi-k2':   'moonshot',
  'kimi-k1.5': 'moonshot',
  // Alibaba / Qwen
  'qwen3.7-max': 'alibaba',
  'qwen3-max':   'alibaba',
  'qwen2.5-max': 'alibaba',
  'qwen2.5-72b': 'alibaba',
  'qwen2.5-32b': 'alibaba',
  'qwen-plus':   'alibaba',
  'qwen-turbo':  'alibaba',
  // Zhipu / GLM
  'glm-5.1':   'zhipu',
  'glm-5':     'zhipu',
  'glm-4':     'zhipu',
  'glm-4v':    'zhipu',
  'chatglm':   'zhipu',
  // Anthropic
  'claude-opus-4.8': 'anthropic',
  'claude-4':        'anthropic',
  'claude-3.5':      'anthropic',
  'claude-3':        'anthropic',
  // OpenAI
  'gpt-5.5':'openai',
  'gpt-4o': 'openai',
  'gpt-4':  'openai',
  'gpt-3.5':'openai',
  'o1':     'openai',
  'o3':     'openai',
  // Google
  'gemini-3.1-pro': 'google',
  'gemini-2.5':     'google',
  'gemini-2':       'google',
  'gemini-1.5':     'google',
};

function lookupProvider(name) {
  if (!name) return null;
  const key = name.toLowerCase().replace(/\s+/g, '');

  // 1. Exact model name → provider
  const direct = MODEL_TO_PROVIDER[key];
  if (direct && PROVIDER_ICONS[direct]) return PROVIDER_ICONS[direct];

  // 2. Prefix match on model keys (longest first)
  const sorted = Object.keys(MODEL_TO_PROVIDER).sort((a, b) => b.length - a.length);
  for (const m of sorted) {
    if (key.startsWith(m) || m.startsWith(key)) {
      const prov = MODEL_TO_PROVIDER[m];
      if (prov && PROVIDER_ICONS[prov]) return PROVIDER_ICONS[prov];
    }
  }

  // 3. Fallback: raw provider name matching
  if (key.includes('anthropic') || key.includes('claude')) return PROVIDER_ICONS.anthropic;
  if (key.includes('openai') || key.includes('gpt') || key.includes('chatgpt') || key.startsWith('o1') || key.startsWith('o3')) return PROVIDER_ICONS.openai;
  if (key.includes('google') || key.includes('gemini')) return PROVIDER_ICONS.google;
  if (key.includes('deepseek')) return PROVIDER_ICONS.deepseek;
  if (key.includes('alibaba') || key.includes('qwen') || key.includes('tongyi')) return PROVIDER_ICONS.alibaba;
  if (key.includes('moonshot') || key.includes('kimi')) return PROVIDER_ICONS.moonshot;
  if (key.includes('zhipu') || key.includes('glm') || key.includes('chatglm')) return PROVIDER_ICONS.zhipu;

  return null;
}

/**
 * LLMIcon component — displays a small icon for the LLM model.
 *
 * Props:
 *   modelName: string — model name (e.g. "deepseek-v4-pro", "qwen3.7-max")
 *   provider: string   — provider name, used as fallback if modelName isn't in MODEL_TO_PROVIDER
 *   size: number       — icon size in pixels (default: 24)
 *   showLabel: boolean — show provider label text (default: false)
 *   className: string  — additional CSS class
 */
function LLMIcon({ provider, modelName, size = 24, showLabel = false, className = '' }) {
  const lookup = modelName || provider;
  const info = lookupProvider(lookup);
  if (!info) return null;

  return (
    <span className={`llm-icon-wrapper ${className}`} title={info.label}>
      <img
        src={info.icon}
        alt={info.label}
        className="llm-icon"
        style={{ width: size, height: size }}
      />
      {showLabel && <span className="llm-icon-label">{info.label}</span>}
    </span>
  );
}

export default LLMIcon;
